/* dl-share.js — design—light · share dialog for the PDP and the cart (2026-08-21)
   Loaded on the product and cart templates (snippets/dl-assets.liquid).
   Changes NO Liquid and does not touch dl-cart.js, so deleting this file, its
   stylesheet and their lines in dl-assets.liquid is the entire rollback.

   -- WHAT THIS REPLACES --------------------------------------------------------
   Both share buttons used navigator.share, which on a Mac opens the OS share
   sheet: a row of contacts and a list of apps, with the link itself nowhere in
   sight. Hynek's word for it was "confusing", and he is right — the one thing a
   salesperson wants is the link, ready to paste. dl-cart.js's last-resort
   fallback was worse still: a raw window.prompt.
   Both now open one styled in-page dialog: the link in a selectable field, and
   a copy button.

   -- HOW THE CART IS REACHED WITHOUT EDITING dl-cart.js ------------------------
   dl-cart.js is 56 KB and has a documented history of failing transcription
   (KOSIK_LIVE_STATE_2026-08-09: three failed uploads across two sessions over a
   single backslash). Rewriting it to change a share dialog is a bad trade.

   Instead this file installs its own navigator.share. dl-cart.js's „Sdílet
   košík" handler already calls navigator.share({title, url}) as its FIRST
   choice, so it now hands us the link it built and we render the dialog. We
   resolve the promise, so its clipboard and window.prompt fallbacks never run.
   navigator.canShare is stubbed true for the same reason.

   This is a deliberate, documented interception, not a global monkey-patch for
   its own sake: it is installed only on the two templates this file loads on,
   and it is the minimal-touch way to own share UI that another file triggers.
   It also means we never duplicate buildShareLink() — the cart link keeps its
   rooms, ordering and selling plans, because dl-cart.js still builds it.

   If dl-cart.js is ever rewritten, the clean move is to have it call
   window.DLShare.open(url, title) directly and to delete installIntercept().

   -- THE PDP LINK IS BUILT, NOT COPIED -----------------------------------------
   origin + pathname + the variant read from the buy form. location.search is
   never read, so the sender's „?dlk=" cart-edit key, utm_*, fbclid and
   preview_theme_id cannot travel, and no cart endpoint is called on either
   side. See PDP_SHARE_BUTTON_2026-08-21.md.

   -- THE QR CODE (2026-10-15, ua-core ideas/2026-10-15-share-dialog-qr.md) -----
   Showroom walk-through on iPads: staff build a cart or open a product, tap
   Sdílet, turn the iPad round and the customer scans the code with a phone. The
   dialog draws a big QR of exactly the shared link under the buttons, black on
   white with a 4-module quiet zone whatever the theme colours, as one SVG path.
   Encoder: assets/dl-qr.js (global `qrcode`, the one the printed plan uses). The
   cart page already loads it (dl-cart-loader.liquid); everywhere else it would
   be 20 KB for a button few people press, so dl-assets.liquid only hands over
   its URL (window.DLShareQR) and loadQr() injects it on the first open. If it
   fails to load, the dialog simply has no code. A link past QR_DENSE bytes gets
   a hint (dense code: bring the phone closer, or AirDrop); past QR_MAX — the
   byte capacity of a version-40 code at level L — or if make() throws, the hint
   alone. */

(function () {
  'use strict';

  var SHARE_IC =
    '<svg class="ic" viewBox="0 0 24 24" aria-hidden="true">' +
    '<circle cx="6" cy="12" r="2.5"></circle>' +
    '<circle cx="18" cy="6" r="2.5"></circle>' +
    '<circle cx="18" cy="18" r="2.5"></circle>' +
    '<path d="m8.2 10.8 7.6-3.6M8.2 13.2l7.6 3.6"></path></svg>';
  var COPY_IC =
    '<svg class="ic" viewBox="0 0 24 24" aria-hidden="true">' +
    '<rect x="9" y="9" width="11" height="11"></rect>' +
    '<path d="M5 15V5a1 1 0 0 1 1-1h9"></path></svg>';
  var TICK_IC =
    '<svg class="ic" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 13 4.5 4.5L19 7"></path></svg>';
  /* the shared ✕ glyph — assets/dl-x.css */
  var X_IC =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';

  var T = {
    eyebrow: 'Sdílet',
    copy: 'Kopírovat odkaz',
    native: 'AirDrop, Zprávy…',
    copied: 'Zkopírováno',
    failed: 'Zkopírujte ručně',
    close: 'Zavřít',
    cartTitle: 'Váš košík',
    productNote: 'Odkaz otevře tento produkt. Košík příjemce se nezmění.',
    cartNote: 'Odkaz sestaví celý tento košík včetně místností. Pozor: příjemci nahradí jeho stávající košík.',
    qrLabel: 'QR kód odkazu',
    dense: 'Velký košík, hustý kód: přibližte telefon, nebo pošlete odkaz přes AirDrop.',
    tooLong: 'Košík je na QR kód příliš velký. Pošlete odkaz přes AirDrop, nebo ho zkopírujte.'
  };

  var QR_DENSE = 1200;
  var QR_MAX = 2953;

  var box = null;
  var lastFocus = null;
  /* The browser's own share sheet, saved before installIntercept() replaces it.
     It is the only way a page can reach AirDrop, so the dialog offers it as a
     second button under Kopírovat — never as the first thing the customer sees. */
  var nativeShare = null;

  /* The cart link (/cart?dlc=1…, or an older /cart/clear -> /cart/add chain)
     REPLACES the recipient's cart. The product link cannot touch a cart at all. One test on
     the URL decides which note is shown, so it stays right regardless of who
     opened the dialog. */
  function isCartLink(url) {
    return url.indexOf('/cart/') > -1 || url.indexOf('/cart?') > -1;
  }

  function close() {
    if (!box) return;
    var el = box;
    box = null;
    el.classList.remove('show');
    document.removeEventListener('keydown', onKey, true);
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    lastFocus = null;
  }

  function onKey(e) {
    if (e.key === 'Escape' || e.keyCode === 27) { e.stopPropagation(); close(); return; }
    if (e.key !== 'Tab' || !box) return;
    /* keep focus inside the dialog while it is open */
    var f = box.querySelectorAll('input, button');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function legacyCopy(input) {
    input.focus();
    input.select();
    try { input.setSelectionRange(0, input.value.length); } catch (e) {}
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    return ok;
  }

  /* ---------- QR code (see header) ---------- */

  var qrWait = null;

  /* calls cb(true) once `qrcode` exists, cb(false) if it cannot be loaded */
  function loadQr(cb) {
    if (typeof qrcode !== 'undefined') { cb(true); return; }
    var src = window.DLShareQR;
    if (!src) { cb(false); return; }
    if (qrWait) { qrWait.push(cb); return; }
    qrWait = [cb];
    var s = document.createElement('script');
    s.src = src;
    s.async = true;
    function settle() {
      var ok = typeof qrcode !== 'undefined', w = qrWait;
      qrWait = null;
      if (!ok) s.parentNode && s.parentNode.removeChild(s);
      for (var i = 0; i < w.length; i++) w[i](ok);
    }
    s.onload = settle;
    s.onerror = settle;
    document.head.appendChild(s);
  }

  /* One path, horizontal runs of dark modules merged; the white square behind it
     is the quiet zone. crispEdges keeps module edges sharp at any scale. */
  function qrSvg(data) {
    var q = qrcode(0, 'L');
    q.addData(data);
    q.make();
    var n = q.getModuleCount(), w = n + 8, d = '';
    for (var r = 0; r < n; r++) {
      for (var c = 0; c < n; c++) {
        if (!q.isDark(r, c)) continue;
        var s = c;
        while (c + 1 < n && q.isDark(r, c + 1)) c++;
        d += 'M' + s + ' ' + r + 'h' + (c - s + 1) + 'v1h-' + (c - s + 1) + 'z';
      }
    }
    return '<svg viewBox="-4 -4 ' + w + ' ' + w + '" shape-rendering="crispEdges" role="img" aria-label="' +
      T.qrLabel + '"><rect x="-4" y="-4" width="' + w + '" height="' + w + '" fill="#fff"></rect>' +
      '<path d="' + d + '" fill="#000"></path></svg>';
  }

  /* The figure, or null when there is nothing to show. Built complete before it
     is returned: theme.css hides every empty div, so nothing goes in empty. */
  function qrBox(url) {
    /* the encoder's default byte mode keeps only the low byte of each char;
       links are ASCII already, this only guards a stray non-ASCII character */
    var data = url.replace(/[^\x00-\x7f]+/g, encodeURIComponent);
    var svg = '';
    if (data.length <= QR_MAX) { try { svg = qrSvg(data); } catch (e) { svg = ''; } }
    var hint = !svg ? T.tooLong : (data.length > QR_DENSE ? T.dense : '');
    var fig = document.createElement('figure');
    fig.className = 'dlsb-qr' + (svg ? '' : ' is-hint');
    fig.innerHTML =
      (svg ? '<span class="dlsb-qr-code">' + svg + '</span>' : '') +
      (hint ? '<p class="dlsb-qr-hint">' + hint + '</p>' : '');
    return fig;
  }

  function open(url, title) {
    if (!url) return;
    close();
    lastFocus = document.activeElement;

    var cart = isCartLink(url);
    var el = document.createElement('div');
    el.className = 'dlsb';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', T.eyebrow);

    /* Sleek's theme.css hides every empty div, so the scrim cannot be its own
       element — it is painted on the container and detected by target compare. */
    el.addEventListener('click', function (ev) { if (ev.target === el) close(); });

    var panel = document.createElement('div');
    panel.className = 'dlsb-panel';

    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'dlsb-x dl-x';
    x.setAttribute('aria-label', T.close);
    x.innerHTML = X_IC;
    x.addEventListener('click', close);

    var eye = document.createElement('span');
    eye.className = 'dlsb-eyebrow';
    eye.textContent = T.eyebrow;

    var h = document.createElement('h2');
    h.className = 'dlsb-t';
    h.textContent = cart ? T.cartTitle : (title || document.title);

    var note = document.createElement('p');
    note.className = 'dlsb-note';
    note.textContent = cart ? T.cartNote : T.productNote;

    var input = document.createElement('input');
    input.className = 'dlsb-url';
    input.type = 'text';
    input.readOnly = true;
    input.value = url;
    input.setAttribute('aria-label', 'Odkaz');
    input.addEventListener('focus', function () { input.select(); input.scrollLeft = 0; });

    var copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'dlsb-copy';
    copy.innerHTML = COPY_IC + '<span>' + T.copy + '</span>';

    var revert = 0;
    copy.addEventListener('click', function () {
      clearTimeout(revert);
      function done(ok) {
        copy.classList.remove('ok', 'err');
        copy.classList.add(ok ? 'ok' : 'err');
        copy.innerHTML = (ok ? TICK_IC : COPY_IC) + '<span>' + (ok ? T.copied : T.failed) + '</span>';
        if (!ok) { input.focus(); input.select(); }
        revert = setTimeout(function () {
          copy.classList.remove('ok', 'err');
          copy.innerHTML = COPY_IC + '<span>' + T.copy + '</span>';
        }, 2000);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () { done(true); },
          function () { done(legacyCopy(input)); });
      } else {
        done(legacyCopy(input));
      }
    });

    /* the text column; the QR is its sibling, so wide landscape screens (iPad
       turned sideways) can set the two side by side (dl-share.css) */
    var main = document.createElement('div');
    main.className = 'dlsb-main';

    panel.appendChild(x);
    main.appendChild(eye);
    main.appendChild(h);
    main.appendChild(note);
    main.appendChild(input);
    main.appendChild(copy);
    panel.appendChild(main);

    if (nativeShare) {
      var nat = document.createElement('button');
      nat.type = 'button';
      nat.className = 'dlsb-copy dlsb-native';
      nat.innerHTML = SHARE_IC + '<span>' + T.native + '</span>';
      nat.addEventListener('click', function () {
        /* cancelling the sheet rejects — that is not an error, the dialog just stays */
        nativeShare({ title: cart ? 'design—light · košík' : (title || document.title), url: url })
          .then(close, function () {});
      });
      main.appendChild(nat);
    }

    el.appendChild(panel);
    document.body.appendChild(el);
    box = el;

    /* the dialog is already usable; the code joins it when the encoder is
       there — at once on the cart page, after one small load elsewhere */
    loadQr(function (ok) {
      if (!ok || box !== el) return;
      var fig = qrBox(url);
      panel.appendChild(fig);
      if (!fig.classList.contains('is-hint')) panel.classList.add('has-qr');
    });

    document.addEventListener('keydown', onKey, true);
    /* Reveal is a forced reflow + synchronous class add, NOT requestAnimationFrame.
       rAF does not fire in a backgrounded or throttled tab, and the first build
       shipped with a double-rAF reveal that left the dialog stuck at opacity 0
       whenever rAF was starved. Reading offsetWidth flushes style so the opacity
       transition still has a start value, and the class lands unconditionally. */
    void el.offsetWidth;
    el.classList.add('show');
    /* focused and pre-selected, so Cmd-C works immediately even if the clipboard
       API is unavailable and the copy button cannot do its job */
    setTimeout(function () {
      try { input.focus(); input.select(); input.scrollLeft = 0; } catch (e) {}
    }, 60);
  }

  /* ---------- PDP button ---------- */

  function buyForm() {
    return document.querySelector('.istick form.dl-buyform') ||
           document.querySelector('.pdp form[action*="/cart/add"]');
  }

  function productUrl() {
    var f = buyForm();
    var el = f && f.querySelector('[name="id"]');
    var id = el && el.value ? String(el.value).trim() : '';
    var url = location.origin + location.pathname;
    if (id) url += '?variant=' + encodeURIComponent(id);
    return url;
  }

  function productTitle() {
    var h = document.querySelector('.istick .ihead h1') || document.querySelector('.pdp h1');
    var t = h ? h.textContent.trim() : '';
    return t || document.title;
  }

  function mount() {
    if (document.querySelector('.dl-share')) return;
    var f = buyForm();
    if (!f || !f.parentNode) return;

    var wrap = document.createElement('div');
    wrap.className = 'dl-share';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-dl-share', '');
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-label', T.eyebrow + ' — ' + productTitle());
    btn.innerHTML = SHARE_IC + '<span>' + T.eyebrow + '</span>';
    btn.addEventListener('click', function () { open(productUrl(), productTitle()); });

    wrap.appendChild(btn);

    /* FUSED INTO THE BUY BLOCK (2026-08-22) ---------------------------------
       `.buy .row` is the grid that holds the quantity stepper and Přidat do
       košíku (112px + 1fr, gap: var(--seam), one 1px --ink border around the
       pair). Appending the button as a third cell — spanned across both
       columns by dl-share.css — puts Sdílet directly under that pair at
       exactly their combined width, with the grid's own hairline as the
       divider. Nothing is measured or offset, so it cannot drift out of line
       at any width, and mount() re-runs after a buy-block re-render.

       If a product template ever renders a buy form without that row, we fall
       back to the original placement below the form, still styled by part 1 of
       dl-share.css. */
    var row = f.querySelector('.buy .row') || f.querySelector('.row');
    if (row) { row.appendChild(wrap); return; }

    f.parentNode.insertBefore(wrap, f.nextSibling);
  }

  /* ---------- cart interception (see header) ---------- */

  function installIntercept() {
    if (navigator.share && !nativeShare) nativeShare = navigator.share.bind(navigator);
    try {
      Object.defineProperty(navigator, 'share', {
        configurable: true,
        writable: true,
        value: function (data) {
          var url = (data && data.url) || '';
          if (!url) return Promise.reject(new Error('no url'));
          open(url, (data && data.title) || document.title);
          return Promise.resolve();
        }
      });
      Object.defineProperty(navigator, 'canShare', {
        configurable: true,
        writable: true,
        value: function () { return true; }
      });
    } catch (e) { /* locked down: the cart keeps its own fallbacks */ }
  }

  function boot() {
    installIntercept();
    mount();
    var host = document.querySelector('.istick') || document.querySelector('.pdp');
    if (!host || !window.MutationObserver) return;
    /* the buy column is re-rendered on some option changes; mount() is
       idempotent, so re-running it is cheap and never doubles the button */
    var t = 0;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(mount, 60);
    }).observe(host, { childList: true, subtree: true });
  }

  window.DLShare = { open: open, close: close };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
